import React from 'react';
import ProfileAssistantRow from '../GeneralProfile/ProfileAssistantRow';
import ProfileUserAnswerRow from '../GeneralProfile/ProfileUserAnswerRow';
import { stepQuestionKey, userAnswerForStep } from './familyInfoSteps';
import type { useFamilyInfoFlow } from './useFamilyInfoFlow';
import type { FamilyStep } from './familyInfoTypes';

type Flow = ReturnType<typeof useFamilyInfoFlow>;

/** Per-member Q&A block (expanded summary or member currently being entered). */
function MemberQASection({
  flow,
  undoLabel,
  editLabel,
  showFieldEdit,
  onEditField,
}: {
  flow: Flow;
  undoLabel: string;
  editLabel: string;
  showFieldEdit?: boolean;
  onEditField?: (step: FamilyStep) => void;
}) {
  const {
    t,
    draft,
    handleUndo,
    relationshipCommitted,
    fullNameCommitted,
    fullNameKanaCommitted,
    dobCommitted,
    sameHouseholdCommitted,
    ifNotAddressCommitted,
    outsideJapanCommitted,
    taxGateCommitted,
    sharedLivelihoodCommitted,
    spouseFamilyWorkerCommitted,
    estimatedIncomeCommitted,
    taxDependentCategoryCommitted,
    taxNonResidentCommitted,
    dependentIneligibleNoticeCommitted,
    disabilityStatusCommitted,
    disabilityFactsCommitted,
    changeDuringYearCommitted,
    nonResidentReasonCommitted,
    nonResidentReasonOtherCommitted,
    remittanceAmountCommitted,
    taxChangeReasonCommitted,
    taxChangeDateCommitted,
    insuranceRequiredCommitted,
    myNumberLinkCommitted,
    supportedDependentCommitted,
    myNumberCommitted,
    showUndoRelationship,
    showUndoFullName,
    showUndoFullNameKana,
    showUndoDob,
    showUndoSameHousehold,
    showUndoIfNotAddress,
    showUndoOutsideJapan,
    showUndoTaxGate,
    showUndoSharedLivelihood,
    showUndoSpouseFamilyWorker,
    showUndoEstimatedIncome,
    showUndoTaxDependentCategory,
    showUndoTaxNonResident,
    showUndoDependentIneligibleNotice,
    showUndoDisabilityStatus,
    showUndoDisabilityFacts,
    showUndoChangeDuringYear,
    showUndoChangeDate,
    showUndoChangeReason,
    showUndoChangeReasonOther,
    showUndoNonResidentReason,
    showUndoNonResidentReasonOther,
    showUndoRemittanceAmount,
    showUndoInsuranceRequired,
    showUndoMyNumberLink,
    showUndoSupportedDependent,
    showUndoMyNumber,
    showUndoAddAnotherBeforeMember,
  } = flow;

  const undoVisible = (flag: boolean) => (showFieldEdit ? false : flag);

  return (
    <>
      {relationshipCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('relationship'))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoRelationship)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('relationship') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {draft.relationship ? t(`submitDocuments.familyInfo.relationship.${draft.relationship}`) : ''}
          </ProfileUserAnswerRow>
        </>
      )}

      {fullNameCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('fullName'))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoFullName)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('fullName') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {draft.fullName.trim()}
          </ProfileUserAnswerRow>
        </>
      )}

      {fullNameKanaCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('fullNameKana'))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoFullNameKana)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('fullNameKana') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {draft.fullNameKana.trim()}
          </ProfileUserAnswerRow>
        </>
      )}

      {dobCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('dob'))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoDob)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('dob') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {draft.dateOfBirth}
          </ProfileUserAnswerRow>
        </>
      )}

      {sameHouseholdCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('sameHousehold'))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoSameHousehold)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('sameHousehold') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('sameHousehold', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {ifNotAddressCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('ifNotAddress'))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoIfNotAddress)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('ifNotAddress') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('ifNotAddress', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {outsideJapanCommitted && draft.relationship === 'spouse' && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('outsideJapan'))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoOutsideJapan)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('outsideJapan') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('outsideJapan', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {taxGateCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('taxGate', draft))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoTaxGate)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('taxGate') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('taxGate', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {sharedLivelihoodCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('sharedLivelihood', draft))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoSharedLivelihood)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('sharedLivelihood') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('sharedLivelihood', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {spouseFamilyWorkerCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('spouseFamilyWorker', draft))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoSpouseFamilyWorker)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('spouseFamilyWorker') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('spouseFamilyWorker', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {estimatedIncomeCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('estimatedIncome', draft))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoEstimatedIncome)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('estimatedIncome') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('estimatedIncome', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {dependentIneligibleNoticeCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('dependentIneligibleNotice', draft))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoDependentIneligibleNotice)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('sharedLivelihood') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('dependentIneligibleNotice', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {taxDependentCategoryCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('taxDependentCategory', draft))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoTaxDependentCategory)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('taxDependentCategory') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('taxDependentCategory', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {taxNonResidentCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('taxNonResident', draft))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoTaxNonResident)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('taxNonResident') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('taxNonResident', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {nonResidentReasonCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('nonResidentReason', draft))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoNonResidentReason)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('nonResidentReason') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('nonResidentReason', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {nonResidentReasonOtherCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('nonResidentReasonOther', draft))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoNonResidentReasonOther)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('nonResidentReasonOther') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('nonResidentReasonOther', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {remittanceAmountCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('remittanceAmount', draft))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoRemittanceAmount)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('remittanceAmount') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('remittanceAmount', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {disabilityStatusCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('disabilityStatus', draft))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoDisabilityStatus)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('disabilityStatus') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('disabilityStatus', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {disabilityFactsCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('disabilityFacts', draft))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoDisabilityFacts)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('disabilityFacts') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('disabilityFacts', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {changeDuringYearCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('changeDuringYear', draft))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoChangeDuringYear)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('changeDuringYear') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('changeDuringYear', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {taxChangeDateCommitted && draft.changeDuringYear === 'yes' && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('changeDate', draft))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoChangeDate)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('changeDate') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('changeDate', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {taxChangeReasonCommitted && draft.changeDuringYear === 'yes' && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('changeReason', draft))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoChangeReason)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('changeReason') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('changeReason', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {insuranceRequiredCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('insuranceRequired'))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoInsuranceRequired)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('insuranceRequired') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('insuranceRequired', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {myNumberLinkCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('myNumberLink'))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoMyNumberLink)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('myNumberLink') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('myNumberLink', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {supportedDependentCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('supportedDependent'))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoSupportedDependent)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('supportedDependent') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('supportedDependent', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}

      {myNumberCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('myNumber'))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={undoVisible(showUndoMyNumber)}
            onUndo={handleUndo}
            showEdit={showFieldEdit}
            onEdit={onEditField ? () => onEditField('myNumber') : undefined}
            undoLabel={undoLabel}
            editLabel={editLabel}
          >
            {userAnswerForStep('myNumber', draft, t)}
          </ProfileUserAnswerRow>
        </>
      )}
    </>
  );
}

export default function FamilyInfoChatThread({ flow }: { flow: Flow }) {
  const {
    t,
    introRevealed,
    hasMembersPromptRevealed,
    introPinned,
    step,
    draft,
    completedMembers,
    expandedMemberIndex,
    inProgressMemberVisible,
    threadEndRef,
    handleUndo,
    hasMembersAnswer,
    hasMembersCommitted,
    addAnother,
    addAnotherCommitted,
    showUndoHasMembers,
    showUndoHasMembersDuringExpand,
    handleUndoSummaryDuringExpand,
    showUndoAddAnother,
    showUndoAddAnotherBeforeMember,
    showUndoAddAnotherAtAddAnother,
    showUndoHasMembersFinal,
    handleEditMemberSummary,
    beginEditMemberField,
    handleUndoMemberSummary,
    showEditOnSummary,
    showUndoOnSummary,
  } = flow;

  const undoLabel = t('submitDocuments.chat.undo');
  const editLabel = t('submitDocuments.fillFormsChecklist.edit');
  const showFieldEdit = step === 'finalConfirm' && expandedMemberIndex !== null;
  const promptStep: FamilyStep =
    step === 'hasMembers' && completedMembers.length > 0 ? 'addAnother' : step;
  const showStepPrompt =
    (step === 'hasMembers' && hasMembersPromptRevealed >= 1 && !hasMembersCommitted && completedMembers.length === 0) ||
    (step === 'relationship' && !flow.relationshipCommitted) ||
    (step === 'fullName' && !flow.fullNameCommitted) ||
    (step === 'fullNameKana' && !flow.fullNameKanaCommitted) ||
    (step === 'dob' && !flow.dobCommitted) ||
    (step === 'sameHousehold' && !flow.sameHouseholdCommitted) ||
    (step === 'ifNotAddress' && !flow.ifNotAddressCommitted) ||
    (step === 'outsideJapan' && draft.relationship === 'spouse' && !flow.outsideJapanCommitted) ||
    (step === 'taxGate' && !flow.taxGateCommitted) ||
    (step === 'sharedLivelihood' && !flow.sharedLivelihoodCommitted) ||
    (step === 'spouseFamilyWorker' && !flow.spouseFamilyWorkerCommitted) ||
    (step === 'estimatedIncome' && !flow.estimatedIncomeCommitted) ||
    (step === 'dependentIneligibleNotice' && !flow.dependentIneligibleNoticeCommitted) ||
    (step === 'taxDependentCategory' && !flow.taxDependentCategoryCommitted) ||
    (step === 'taxNonResident' && !flow.taxNonResidentCommitted) ||
    (step === 'nonResidentReason' && !flow.nonResidentReasonCommitted) ||
    (step === 'nonResidentReasonOther' && !flow.nonResidentReasonOtherCommitted) ||
    (step === 'disabilityStatus' && !flow.disabilityStatusCommitted) ||
    (step === 'disabilityFacts' && !flow.disabilityFactsCommitted) ||
    (step === 'changeDuringYear' && !flow.changeDuringYearCommitted) ||
    (step === 'changeDate' && !flow.taxChangeDateCommitted) ||
    (step === 'changeReason' && !flow.taxChangeReasonCommitted) ||
    (step === 'changeReasonOther' && !flow.taxChangeReasonCommitted) ||
    (step === 'remittanceAmount' && !flow.remittanceAmountCommitted) ||
    (step === 'insuranceRequired' && !flow.insuranceRequiredCommitted) ||
    (step === 'myNumberLink' && !flow.myNumberLinkCommitted) ||
    (step === 'supportedDependent' && !flow.supportedDependentCommitted) ||
    (step === 'myNumber' && !flow.myNumberCommitted) ||
    ((step === 'addAnother' || (step === 'hasMembers' && completedMembers.length > 0)) &&
      !addAnotherCommitted &&
      expandedMemberIndex === null);

  const hasMembersLabel =
    hasMembersAnswer === 'yes'
      ? t('submitDocuments.familyInfo.yes')
      : hasMembersAnswer === 'no'
        ? t('submitDocuments.familyInfo.no')
        : '';

  return (
    <div className="gen-profile-chat__thread" role="log" aria-live="polite">
      {(introPinned || introRevealed >= 1) && (
        <ProfileAssistantRow animateIn={introRevealed >= 1}>{t('submitDocuments.familyInfo.intro')}</ProfileAssistantRow>
      )}

      {hasMembersCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('hasMembers'))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={showUndoHasMembers || showUndoHasMembersDuringExpand || showUndoHasMembersFinal}
            onUndo={handleUndo}
            undoLabel={undoLabel}
          >
            {hasMembersLabel}
          </ProfileUserAnswerRow>
        </>
      )}

      {completedMembers.map((entry, i) => {
        return (
          <React.Fragment key={`member-slot-${i}`}>
            {expandedMemberIndex === i ? (
              <MemberQASection
                flow={flow}
                undoLabel={undoLabel}
                editLabel={editLabel}
                showFieldEdit={showFieldEdit}
                onEditField={beginEditMemberField}
              />
            ) : (
              <ProfileUserAnswerRow
                undoLabel={undoLabel}
                editLabel={editLabel}
                showEdit={showEditOnSummary(i)}
                onEdit={() => handleEditMemberSummary(i)}
                showUndo={showUndoOnSummary(i)}
                onUndo={() =>
                  expandedMemberIndex !== null
                    ? handleUndoSummaryDuringExpand(i)
                    : handleUndoMemberSummary(i)
                }
              >
                {entry.text}
              </ProfileUserAnswerRow>
            )}
          </React.Fragment>
        );
      })}

      {addAnotherCommitted && (
        <>
          <ProfileAssistantRow>{t(stepQuestionKey('addAnother'))}</ProfileAssistantRow>
          <ProfileUserAnswerRow
            showUndo={showUndoAddAnother || showUndoAddAnotherBeforeMember || showUndoAddAnotherAtAddAnother}
            onUndo={handleUndo}
            undoLabel={undoLabel}
          >
            {addAnother === 'yes' ? t('submitDocuments.familyInfo.yes') : t('submitDocuments.familyInfo.no')}
          </ProfileUserAnswerRow>
        </>
      )}

      {inProgressMemberVisible && (
        <MemberQASection flow={flow} undoLabel={undoLabel} editLabel={editLabel} />
      )}

      {showStepPrompt && <ProfileAssistantRow animateIn>{t(stepQuestionKey(promptStep, draft))}</ProfileAssistantRow>}

      {step === 'finalConfirm' && (
        <ProfileAssistantRow animateIn>{t('submitDocuments.familyInfo.finalConfirm')}</ProfileAssistantRow>
      )}

      <div ref={threadEndRef} />
    </div>
  );
}
